import React, { useState } from 'react';
import { Badge, Button, Card, Collapse } from 'react-bootstrap';
import { ChevronDown, ChevronRight, Eye, Search } from 'react-bootstrap-icons';
import { CalibrationVideo, Camera } from '../../types';

interface CameraCardProps {
    camera: Camera;
    onDelete: (camera: Camera) => void;
    onSetCalibrationVideo: (camera: Camera, videoId: number) => void;
    onStartCalibration: (video: CalibrationVideo) => void;
    onViewDetails: (video: CalibrationVideo) => void;
}

const CameraCard: React.FC<CameraCardProps> = ({
    camera,
    onDelete,
    onSetCalibrationVideo,
    onStartCalibration,
    onViewDetails,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <Card className="mb-3">
            <Card.Header
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: 'pointer' }}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="d-flex align-items-center gap-2">
                    {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    <div>
                        <h6 className="mb-0">{camera.name}</h6>
                        <small className="text-muted">{camera.unique_id}</small>
                    </div>
                </div>
                <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete(camera);
                    }}
                    title="Delete camera"
                >
                    Delete Camera
                </Button>
            </Card.Header>
            <Collapse in={isExpanded}>
                <Card.Body>
                    <h6>Calibration Videos</h6>
                    {camera.calibration_videos && camera.calibration_videos.length > 0 ? (
                        <div>
                            <ul className="list-group">
                                {camera.calibration_videos.map((video) => (
                                    <li key={video.id} className="list-group-item d-flex justify-content-between align-items-center">
                                        <div>
                                            {video.title}
                                            {camera.used_calibration_video && camera.used_calibration_video.id === video.id && (
                                                <Badge bg="success" className="ms-2">Active</Badge>
                                            )}
                                            <Badge
                                                bg={
                                                    video.status === 'completed' ? 'success' :
                                                        video.status === 'error' ? 'danger' :
                                                            video.status === 'canceled' ? 'secondary' :
                                                                'warning'
                                                }
                                                className="ms-2"
                                            >
                                                {video.status.charAt(0).toUpperCase() + video.status.slice(1)}
                                            </Badge>
                                            {video.calibrator_type && (
                                                <Badge bg="info" className="ms-2">
                                                    {video.calibrator_type === 'advanced' ? 'Advanced' : 'Basic'}
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="d-flex gap-2">
                                            <Button
                                                variant={camera.used_calibration_video && camera.used_calibration_video.id === video.id ? 'outline-warning' : 'outline-primary'}
                                                size="sm"
                                                onClick={() => onSetCalibrationVideo(camera, video.id)}
                                            >
                                                {camera.used_calibration_video && camera.used_calibration_video.id === video.id ? 'Unset' : 'Set as Active'}
                                            </Button>
                                            <Button
                                                variant="outline-success"
                                                size="sm"
                                                onClick={() => onStartCalibration(video)}
                                                title="Start calibration orchestrator"
                                            >
                                                <Search size={16} />
                                            </Button>
                                            <Button variant="outline-secondary" onClick={() => onViewDetails(video)}>
                                                <Eye size={16} />
                                            </Button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : (
                        <p className="text-muted">No calibration videos</p>
                    )}
                </Card.Body>
            </Collapse>
        </Card>
    );
};

export default CameraCard;
